import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { EntityLink } from "@/components/EntityLink";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Trash2, Wifi } from "lucide-react";

const STATUSES = ["nuova", "in_lavorazione", "firmata", "in_attivazione", "attivata", "ko", "annullata"];
const STATUS_LABEL = { firmata: "Firma cliente verificata", in_attivazione: "In attivazione", ko: "Non attivabile", nuova: "Nuova", in_lavorazione: "In lavorazione", attivata: "Attivata", annullata: "Annullata" };

export default function AdminInternetRequests() {
  const { user } = useAuth();
  const admin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [params, setParams] = useSearchParams();
  const focus = params.get("cerca");
  const shown = focus ? rows.filter((r) => r.id === focus) : rows;
  const load = () => api.get("/internet/requests").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    if (status === "firmata" && !window.confirm("Confermi di aver verificato la firma del cliente sul contratto? La provvigione sarà mostrata in arrivo, senza accredito anticipato.")) return;
    try { const { data } = await api.put(`/admin/internet/requests/${id}/status`, { status }); toast.success(data?.trashed ? "Annullata: spostata nel Cestino" : "Stato aggiornato"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore"); }
  };
  const trash = async (r) => {
    if (!window.confirm("Spostare la richiesta nel cestino? Potrai ripristinarla entro 60 giorni.")) return;
    try { await api.delete(`/internet/requests/${r.id}`); toast.success("Richiesta spostata nel cestino"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Eliminazione non riuscita"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-internet-page">
      <div>
        <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Connettività</p>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-1 flex items-center gap-2"><Wifi className="w-7 h-7 text-primary" /> {admin ? "Richieste Internet" : "Le mie richieste Internet"}</h1>
      </div>
      {focus && <div className="flex items-center gap-3 text-sm" data-testid="internet-search-focus"><span className="text-muted-foreground">Risultato della ricerca</span><Button size="sm" variant="outline" onClick={() => setParams({})} data-testid="internet-search-show-all">Mostra tutte</Button></div>}
      <Card className="glow-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Cliente</TableHead><TableHead>Tipo</TableHead><TableHead>Tecnologia</TableHead>
            <TableHead>Offerta</TableHead><TableHead>Indirizzo</TableHead><TableHead>Stato</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {shown.map((r) => (
              <TableRow key={r.id} data-testid={`internet-req-${r.id}`}>
                <TableCell className="font-medium"><EntityLink kind="customer" id={r.customer_id} data-testid={`internet-customer-link-${r.id}`}>{r.ragione_sociale || [r.nome, r.cognome].filter(Boolean).join(" ") || "—"}</EntityLink><span className="block text-xs text-muted-foreground">{r.email}</span></TableCell>
                <TableCell><Badge variant="outline" className="capitalize">{r.customer_type}</Badge></TableCell>
                <TableCell>{r.tech_label || r.technology}</TableCell>
                <TableCell><EntityLink kind="internet" id={r.id} data-testid={`internet-practice-link-${r.id}`}>{r.offer_name || "Apri richiesta"}</EntityLink> · {euro(r.offer_price)}</TableCell>
                <TableCell className="max-w-[200px] truncate">{r.address}</TableCell>
                <TableCell className="flex items-center gap-2">
                  {admin ? <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                    <SelectTrigger className="w-40" data-testid={`internet-status-${r.id}`}><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`internet-status-option-${r.id}-${s}`}>{STATUS_LABEL[s]}</SelectItem>)}</SelectContent>
                  </Select> : <Badge variant="outline" data-testid={`internet-status-${r.id}`}>{STATUS_LABEL[r.status] || r.status}</Badge>}
                  {(admin ? !["firmata", "in_attivazione", "attivata"].includes(r.status) : r.status === "nuova") && <Button size="icon" variant="ghost" className="h-8 w-8 text-red-500" aria-label="Sposta nel cestino" title="Sposta nel cestino" onClick={() => trash(r)} data-testid={`internet-trash-${r.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">Nessuna richiesta.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
